import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import InlineSkillsTable from '../Components/InlineSkillsTable';
import Table from '../Components/Tables';
import { Tab, Tabs } from '../Components/Tabs';

import { useAlert } from '../Components/AlertContext';
import { ConfirmationProvider, useConfirmation } from '../Components/ConfirmationContext';
import { PromptApi, PromptApiList, PromptCreateApi } from '../Services/PromptApiProviderTypes';
import { formatDateTimeLocal } from '../utils/formatDate';
import { PromptApiProvider, usePromptApi } from '../Services/PromptApiProvider';
import { InlineSkillApiProvider } from '../Services/InlineSkillApiProvider';
import useTablePagination from '../Hooks/useTablePagination';

const TABS = {
  PROMPTS: 'prompts',
  INLINE_SKILLS: 'inline-skills',
};

function PromptsHub({ canEdit }: { canEdit: boolean }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { organizationId } = useParams();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();

  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab =
    searchParams.get('tab') === TABS.INLINE_SKILLS ? TABS.INLINE_SKILLS : TABS.PROMPTS;

  const handleTabChange = useCallback(
    (value: string) => {
      const nextParams = new URLSearchParams(searchParams);
      if (value === TABS.PROMPTS) nextParams.delete('tab');
      else nextParams.set('tab', value);
      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const { getPrompts, deletePrompt, createPrompt } = usePromptApi();

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [prompts, setPrompts] = useState<PromptApi[]>([]);
  const [totalPrompts, setTotalPrompts] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [alertShown, setAlertShown] = useState<boolean>(false);

  const [selectedRows, setSelectedRows] = useState<PromptApi[]>([]);
  const selectedId = selectedRows.length === 1 ? selectedRows[0].id : null;

  const tableTitle = 'Prompts';
  const { initialPageSize, storedSkip, storedLimit } = useTablePagination(tableTitle);

  const columns = [
    { name: 'ID', selector: (row: PromptApi) => row.id, sortable: false },
    { name: 'Name', selector: (row: PromptApi) => row.name, sortable: false },
    {
      name: 'Created',
      selector: (row: PromptApi) => formatDateTimeLocal(row.created_at),
      sortable: false,
    },
    {
      name: 'Updated',
      selector: (row: PromptApi) => formatDateTimeLocal(row.updated_at),
      sortable: false,
    },
  ];

  // Display alert message on navigation
  useEffect(() => {
    if (location.state?.successMessage && !alertShown) {
      showAlert('success', location.state.successMessage);
      setAlertShown(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
    if (location.state?.returnMessage && !alertShown) {
      showAlert('help', location.state.returnMessage);
      setAlertShown(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.successMessage, alertShown, location.pathname]);

  const handleDelete = async (selectedId: string[]): Promise<void> => {
    if (!selectedId[0]) return;
    showConfirmation(`Are you sure you want to delete the selected prompt?`, async () => {
      try {
        setLoading(true);
        await deletePrompt(selectedId[0]);
        featchPrompts(storedSkip(), storedLimit());
        showAlert('success', 'Prompt was successfully deleted!');
      } catch (error) {
        showAlert('fail', 'Failed to delete the prompt');
      } finally {
        setLoading(false);
      }
    });
  };

  const createCopy = async (): Promise<void> => {
    if (!selectedId) return;
    try {
      setLoading(true);
      const promptToCopy = prompts.find((prompt) => prompt.id === selectedId);

      if (!promptToCopy) {
        showAlert('fail', 'Prompt not found');
        setLoading(false);
        return;
      }
      const baseName = promptToCopy.name;
      const copyNumbers = prompts
        .map((a) => {
          const match = a.name.match(new RegExp(`^${baseName} \\(Copy (\\d+)\\)$`));
          return match ? parseInt(match[1], 10) : null;
        })
        .filter((n) => n !== null);
      const nextCopyNumber = copyNumbers.length > 0 ? Math.max(...copyNumbers) + 1 : 1;

      const newPromptData: PromptCreateApi = {
        name: `${baseName} (Copy ${nextCopyNumber})`,
        pattern_type: promptToCopy.pattern_type,
        definition: promptToCopy.definition,
        sort_order: promptToCopy.sort_order ?? 0,
      };
      await createPrompt(newPromptData);
      featchPrompts(storedSkip(), storedLimit());
      showAlert('success', 'Prompt copy was successfully created!');
    } catch (error) {
      showAlert('fail', 'Failed to create a copy of the prompt');
    } finally {
      setLoading(false);
    }
  };

  const handleRowClicked = (row: PromptApi): void => {
    navigate(`/organizations/${organizationId}/prompts-hub/${row.id}`);
  };

  const featchPrompts = async (
    skip: number,
    limit: number,
    search: string = searchQuery,
  ): Promise<void> => {
    setLoading(true);

    try {
      const response: PromptApiList = await getPrompts(skip, limit, search.trim() || undefined);
      setPrompts(response.items);
      setTotalPrompts(response.totalCount); // Set the total number of prompts
    } catch (error) {
      console.error('Error fetching prompts:', error);
      setError(error.message); // Set error message
    } finally {
      setLoading(false); // Stop loading
    }
  };

  const onSearch = (skip: number, limit: number, search: string) => {
    setSearchQuery(search);
    featchPrompts(skip, limit, search);
  };

  useEffect(() => {
    featchPrompts(storedSkip(), storedLimit());
  }, []);

  return (
    <div className="container">
      <Breadcrumbs />
      <Tabs activeTab={activeTab} onTabChange={handleTabChange}>
        <Tab label="Prompts" value={TABS.PROMPTS}>
          <div className="row d-flex gap">
            <div className="radius-yes p-2 bg-yes col-12">
              {error ? (
                <div>Error: {error}</div>
              ) : (
                <>
                  <Table
                    title={tableTitle}
                    columns={columns}
                    data={prompts} // Render table only when data is available
                    selectableRows
                    selectableRowsHighlight
                    progressPending={loading}
                    paginationPerPage={initialPageSize}
                    paginationTotalRows={totalPrompts}
                    disabled={false}
                    onRowClicked={handleRowClicked}
                    onDelete={handleDelete}
                    onChangePage={featchPrompts}
                    typeSearch="default"
                    searchOnEnter
                    onChangeFilter={onSearch}
                    getRowUrl={(row) => `/organizations/${organizationId}/prompts-hub/${row.id}`}
                    onSelectedRowsChange={({ selectedRows }) => {
                      setSelectedRows(selectedRows);
                    }}
                  />
                  {canEdit && (
                    <Button
                      background="color2"
                      size="large"
                      onClick={() =>
                        navigate(`/organizations/${organizationId}/prompts-hub/new-prompt`)
                      }
                    >
                      Create New Prompt
                    </Button>
                  )}
                  {selectedId && (
                    <Button variant="ghost" size="medium" onClick={createCopy}>
                      Create copy
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        </Tab>
        <Tab label="Inline Skills" value={TABS.INLINE_SKILLS}>
          <div className="row d-flex gap">
            <div className="radius-yes p-2 bg-yes col-12">
              <InlineSkillsTable canEdit={canEdit} />
            </div>
          </div>
        </Tab>
      </Tabs>
    </div>
  );
}

function PromptsHubWrapper({ canEdit }: { canEdit: boolean }) {
  return (
    <ConfirmationProvider>
      <PromptApiProvider>
        <InlineSkillApiProvider>
          <PromptsHub canEdit={canEdit} />
        </InlineSkillApiProvider>
      </PromptApiProvider>
    </ConfirmationProvider>
  );
}

export default PromptsHubWrapper;
